/**
 * This file contains the exported merge function and keyArgs arrays that are the common approach used
 * across all of our paginated queries.
 *
 * If you need to have specific configuration for a query, create your own merge function and keyArgs array with your required config.
 *
 * Please do not modify this file's config unless specifically told to, as it may have unintended consequences across all of our paginated queries.
 */

/**
 * Default configuration of paginated query results.
 * This is used for all paginated queries that do not have their own specific configuration.
 */
export const paginatedComboboxQueryResultsKeyArgs = ['input', ['where']];

/**
 * Default configuration of paginated query results that have page input.
 * This is used for all paginated queries that have page input and do not have their own specific configuration.
 */
export const mergedPaginatedQueryResultsKeyArgs = [
  'input',
  ['where', 'pageInput', ['sort'], 'pageInputWithFiveHundredLimit', ['sort']],
];

type PaginatedQueryResult<Edge> = {
  edges?: ReadonlyArray<Edge> | null;
  [key: string]: unknown;
};

type PaginationMergeOptions = {
  args?: {
    input?: {
      pageInput?: {
        number?: number | null;
      } | null;
      pageInputWithFiveHundredLimit?: {
        number?: number | null;
      } | null;
    } | null;
  } | null;
  readField?(fieldName: string, from?: unknown): unknown;
};

/**
 * Reads the stable GraphQL entity id from an edge using Apollo's `readField` helper.
 * `readField` is required here because cached edges may be references rather than plain objects.
 */
function getEdgeId(
  edge: unknown,
  readField: PaginationMergeOptions['readField']
): string | undefined {
  const id = readField?.('id', edge);

  return typeof id === 'string' || typeof id === 'number'
    ? String(id)
    : undefined;
}

/**
 * Appends incoming edges without allowing the same entity id to appear twice.
 * When an incoming edge already exists in the cached list, the incoming edge replaces the old one
 * so refetched pages keep the latest entity data while preserving the original list position.
 */
function mergeUniqueEdges<Edge>(
  existingEdges: ReadonlyArray<Edge>,
  incomingEdges: ReadonlyArray<Edge>,
  readField: PaginationMergeOptions['readField']
) {
  const mergedEdges = [...existingEdges];
  const edgeIndexById = new Map<string, number>();

  mergedEdges.forEach((edge, index) => {
    const id = getEdgeId(edge, readField);

    if (id) {
      edgeIndexById.set(id, index);
    }
  });

  incomingEdges.forEach((edge) => {
    const id = getEdgeId(edge, readField);

    if (!id) {
      mergedEdges.push(edge);
      return;
    }

    const existingIndex = edgeIndexById.get(id);

    if (existingIndex === undefined) {
      edgeIndexById.set(id, mergedEdges.length);
      mergedEdges.push(edge);
      return;
    }

    mergedEdges[existingIndex] = edge;
  });

  return mergedEdges;
}

/**
 * Default merge function for paginated query results.
 * This is used for all paginated queries that do not have their own specific merge function.
 */
export function mergePaginatedQueryResults<Edge>(
  existing: PaginatedQueryResult<Edge> | null | undefined,
  incoming: PaginatedQueryResult<Edge> | null | undefined,
  options: PaginationMergeOptions
) {
  if (!options?.args) {
    throw Error('Cannot merge paginated field without pagination variables');
  }

  if (!incoming) {
    return existing;
  }

  const incomingEdges = incoming.edges || [];

  const pageNumber =
    options.args.input?.pageInput?.number ??
    options.args.input?.pageInputWithFiveHundredLimit?.number;

  if (!existing || pageNumber === 1) {
    return {
      ...incoming,
      edges: mergeUniqueEdges([], incomingEdges, options.readField),
    };
  }

  const existingEdges = existing.edges || [];

  return {
    ...incoming,
    edges: mergeUniqueEdges(existingEdges, incomingEdges, options.readField),
  };
}
